import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import {
  EntityServiceClient,
  LevelDto,
  LevelFilterDto,
  LevelServiceClient,
  PredictionModelDto,
  PredictionModelFilterDto,
  ReferenceDto,
  ReferenceFilterDto
} from '@sharedrest/data.restservice';
import { UserService } from '@sharedcommon/services/user.service';
import { Constants } from '@sharedcommon/util/constants.util';
import { FunctionUtils } from '@sharedcommon/util/functions.util';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BusinessUnitRestService } from '@sharedcommon/rest/organization/business-unit.restservice';
import { SupplierRestService } from '@sharedcommon/rest/organization/supplier.restservice';
import { SupplierFilterDto } from '@sharedcommon/model/organization/filter/supplier-filter.model';
import { BusinessUnitFilterDto } from '@sharedcommon/model/organization/filter/business-unit-filter.model';
import { BusinessUnitDto } from '@sharedcommon/model/organization/business-unit.model';
import { SupplierDto } from '@sharedcommon/model/organization/supplier.model';
import { SampleFilterDto } from '@sharedcommon/model/sample/filter/sample-filter.model';
import { SampleRestService } from '@sharedcommon/rest/sample/sample.restservice';
import { SampleDashboardDto } from '@sharedcommon/model/sample/sample-dashboard.model';
import { SampleAdvancedSearchCriterias } from '@sharedcommon/model/sample-advanced-search-criterias.model';
import { SampleAdvancedSearchService } from '@usercommon/service/sample-advanced-search.service';
import { Observable, of } from 'rxjs';
import { MultiSelectAutoCompleteModel } from '@usercommon/components/advanced-samples-search/advanced-sample-multiple-select/advanced-sample-multiple-select.model';
import { AdvancedSampleMultipleAutocompleteComponent } from './advanced-sample-multiple-select/advanced-sample-multiple-select.component';
import { EntityDto, SubEntityServiceClient } from '@sharedcommon/rest/identity.restservice';
import { SpeciesDto } from '@sharedcommon/model/sample/species.model';
import { SpeciesRestService } from '@sharedcommon/rest/sample/species.restservice';
import { SpeciesFilterDto } from '@sharedcommon/model/sample/filter/species-filter.model';
import { SampleDto } from '@sharedcommon/model/sample/sample.model';
import { NeqSelectAutocompleteComponent } from '@sharedcommon/components/select-autocomplete/neq-select-autocomplete.component';

const NB_ELEMENT_PER_PAGE = 10;
const ENTITY = 'entity';
const BUSINESS_UNIT = 'business-unit';
const SUPPLIER = 'supplier';
const PRED_MOD = 'prediction-model';
const REFERENCE = 'reference';
const SAMPLE = 'sample';
const FARM = 'farm';
const DISTRIBUTOR = 'distributor';
const TECHNICIAN = 'technician';
const SPECIES = 'species';
const DATE = "date";
const LEVEL = "level";


class SelectionItem<T> {
  label: string;
  data: T;

  constructor(data: T, code: string, nameOrLabel?: string, description?: string) {
    this.data = data;
    this.label = `${code}`  + (FunctionUtils.isDefined(nameOrLabel) ? ` - ${nameOrLabel}` : '') + (description ? ` (${description})` : '') ;
  }
}

class MultiSelect<T> extends MultiSelectAutoCompleteModel<SelectionItem<T>> {
  /**
   * Return the array of selected data present in the attribute allSelectedValues
   * @returns the selected data
   */
  getSelectedDatas(): T[] {
    return (this.allSelectedValues || []).map(value => value.data);
  }
}

@Component({
  styleUrls: [ 'advanced-samples-search-popin.component.scss' ],
  templateUrl: 'advanced-samples-search-popin.component.html'
})
export class NeqAdvancedSamplesSearchPopinComponent implements OnInit {

  @ViewChild('selectEntity') autocompleteEntity: AdvancedSampleMultipleAutocompleteComponent<any>;
  @ViewChild('selectSupplier') autocompleteSupplier: AdvancedSampleMultipleAutocompleteComponent<any>;
  @ViewChild('selectPredictionModel') autocompletePredMod: AdvancedSampleMultipleAutocompleteComponent<any>;
  @ViewChild('selectReference') autocompleteReference: AdvancedSampleMultipleAutocompleteComponent<any>;
  @ViewChild('selectSample') autocompleteSample: AdvancedSampleMultipleAutocompleteComponent<any>|NeqSelectAutocompleteComponent;
  @ViewChild('selectSpecies') autocompleteSpecies: AdvancedSampleMultipleAutocompleteComponent<any>;
  @ViewChild('selectFarm') autocompleteFarm: AdvancedSampleMultipleAutocompleteComponent<any>;
  @ViewChild('selectDistributor') autocompleteDistributor: AdvancedSampleMultipleAutocompleteComponent<any>;
  @ViewChild('selectTechnician') autocompleteTechnician: AdvancedSampleMultipleAutocompleteComponent<any>;

  allLevels: LevelDto[] = [];
  /** Raw selection of the tree node component */
  allSelectedLevels: LevelDto[] = [];
  /** Contains the selected levels which are leaves of the level tree -> level with no children or null children */
  allFilteredSelectedLevels: LevelDto[] = [];
  entityCode: string;
  businessUnitSelection: MultiSelect<BusinessUnitDto>;
  entitySelection: MultiSelect<EntityDto>;
  supplierSelection: MultiSelect<SupplierDto>;
  predModSelection: MultiSelect<PredictionModelDto>;
  refSelection: MultiSelect<ReferenceDto>;
  sampleSelection: MultiSelect<SampleDashboardDto>;
  farmSelection: MultiSelect<string>;
  distributorSelection: MultiSelect<string>;
  technicianSelection: MultiSelect<string>;
  speciesSelection: MultiSelect<SpeciesDto>;

  startValidation: Date;
  endValidation: Date;
  startReception: Date;
  endReception: Date;

  /**
   * indicates if the advanced search is for one sample
   * and not to update the advance search update of the dashoboard
   */
  singleSelectionMode: boolean;

  constructor(
    private readonly sampleAdvancedSearch: SampleAdvancedSearchService,
    private readonly supplierService: SupplierRestService,
    private readonly businessUnitService: BusinessUnitRestService,
    private readonly userService: UserService,
    private readonly subEntityService: SubEntityServiceClient,
    private readonly levelService: LevelServiceClient,
    private readonly speciesService: SpeciesRestService,
    private readonly entityService: EntityServiceClient,
    private readonly sampleService: SampleRestService,
    private readonly dialogRef: MatDialogRef<NeqAdvancedSamplesSearchPopinComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {singleSelectionMode: boolean}
  ) {
    this.singleSelectionMode = !!data?.singleSelectionMode;
  }

  ngOnInit() {
    this.entityCode = this.userService.getEntity();

    this.supplierSelection = new MultiSelect<SupplierDto>(SUPPLIER, 'label.common.laboratories', 'label.common.laboratories', this.fetchSupplier);
    this.entitySelection = new MultiSelect<EntityDto>(ENTITY, 'label.common.entities', 'label.common.entities', this.fetchAssociatedEntities);
    this.businessUnitSelection = new MultiSelect<BusinessUnitDto>(BUSINESS_UNIT, 'label.common.business-units', 'label.common.business-units', this.fetchBusinessUnit);
    this.predModSelection = new MultiSelect<PredictionModelDto>(PRED_MOD, 'label.common.prediction-models', 'label.common.prediction-models', this.fetchPredMod);
    this.refSelection = new MultiSelect<ReferenceDto>(REFERENCE, 'label.common.references', 'label.common.references', this.fetchReference);
    this.sampleSelection = new MultiSelect<SampleDashboardDto>(SAMPLE, 'label.common.samples', 'label.common.samples', this.fecthSample);
    this.farmSelection = new MultiSelect<string>(FARM, 'label.common.farms', 'label.common.farms', this.fecthFarmName);
    this.distributorSelection = new MultiSelect<string>(DISTRIBUTOR, 'label.common.distributors', 'label.common.distributors', this.fecthDistributors);
    this.technicianSelection = new MultiSelect<string>(TECHNICIAN, 'label.common.technicians', 'label.common.technicians', this.fecthTechnician);
    this.speciesSelection = new MultiSelect<SpeciesDto>(SPECIES, 'label.common.species', 'label.common.species', this.fecthSpecies);

    if (!this.singleSelectionMode) {
      // Restore the former criteria only if the search is for dashboard
      this.restoreTheFormerCriteria();
    }

    this.updateLevelChoice()
  }

  updateLevelChoice(): void {
    const filter = new LevelFilterDto();
    filter.page = Constants.ALL_PAGE;
    filter.withChildren = true;
    filter.depth = 2;
    this.levelService.fetch(filter).subscribe(pagedLevels => {
      this.allLevels = pagedLevels.data;
    });
  }

  /**
   * Restore the former advanced search that the user has previously validated
   */
  restoreTheFormerCriteria(): void {
    const storedCriterias = this.sampleAdvancedSearch.returnCurrentAdvancedSearchValue();
    if (storedCriterias) {
      this.supplierSelection.allSelectedValues = (storedCriterias.suppliers ?? []).map(item => new SelectionItem(item, item.code, item.label));
      this.entitySelection.allSelectedValues = (storedCriterias.entities ?? []).map(item => new SelectionItem(item, item.code, item.name));
      this.businessUnitSelection.allSelectedValues = (storedCriterias.businessUnits ?? []).map(item => new SelectionItem(item, item.code, item.label));
      this.predModSelection.allSelectedValues = (storedCriterias.predictionModels ?? []).map(item => new SelectionItem(item, item.code, item.label));
      this.refSelection.allSelectedValues = (storedCriterias.references ?? []).map(item => new SelectionItem(item, item.code, item.label));
      this.sampleSelection.allSelectedValues = (storedCriterias.samples ?? []).map(item => new SelectionItem(item, item.code, item.label));
      this.farmSelection.allSelectedValues = (storedCriterias.farmNames ?? []).map(item => new SelectionItem(item, item));
      this.distributorSelection.allSelectedValues = (storedCriterias.distributorNames ?? []).map(item => new SelectionItem(item, item));
      this.speciesSelection.allSelectedValues = (storedCriterias.allSpecies ?? []).map(item => new SelectionItem(item, item.code, item.label));
      this.technicianSelection.allSelectedValues = (storedCriterias.technicianNames ?? []).map(item => new SelectionItem(item, item));
      this.allSelectedLevels = storedCriterias.levels ?? [];
      this.allFilteredSelectedLevels = storedCriterias.filteredLevels ?? [];
      this.startReception = storedCriterias.startReceptionDate;
      this.endReception = storedCriterias.endReceptionDate;
      this.startValidation = storedCriterias.startValidationDate;
      this.endValidation = storedCriterias.endValidationDate;
    }
  }

  onSelectionRefreshed(multiSelect: MultiSelect<any>): void {
    this.resetAutoCompleteAccordingIdentifier(multiSelect.identifier);
  }

  onLevelSelection() {
    /* Filter the selected levels to only keep the last depth level (no children or null children) */
    this.allFilteredSelectedLevels = this.allSelectedLevels.filter(selected => !selected.children || selected.children.length === 0);
    this.resetAutoCompleteAccordingIdentifier(LEVEL);
  }

  onDateSelection(): void {
    this.resetAutoCompleteAccordingIdentifier(DATE);
  }

  /**
   * Reset the auto completion component according the field name
   * @param updatedIdentifier the field name which has been updated
   */
  resetAutoCompleteAccordingIdentifier(updatedIdentifier: string): void {
    switch(updatedIdentifier) {
      case BUSINESS_UNIT:
        this.resetAllAutoComplete([ this.autocompleteSupplier, this.autocompleteEntity, this.autocompleteSample ]);
        break;
      case ENTITY:
        this.resetAllAutoComplete([ this.autocompleteSupplier, this.autocompleteSample ]);
        break;
      case SUPPLIER:
        this.resetAllAutoComplete([ this.autocompleteSample ]);
        break;
      case LEVEL:
        this.resetAllAutoComplete([
          this.autocompleteSample,
          this.autocompleteReference,
          this.autocompletePredMod,
          this.autocompleteFarm,
          this.autocompleteDistributor,
          this.autocompleteSpecies,
          this.autocompleteTechnician
        ]);
        break;
      case PRED_MOD:
        this.resetAllAutoComplete([
          this.autocompleteReference,
          this.autocompleteSample,
          this.autocompleteFarm,
          this.autocompleteTechnician,
          this.autocompleteDistributor,
          this.autocompleteSpecies
        ]);
        break;
      case SPECIES:
      case REFERENCE:
        this.resetAllAutoComplete([
          this.autocompleteSample,
          this.autocompleteFarm,
          this.autocompleteDistributor,
          this.autocompleteTechnician
        ]);
        break;
      case TECHNICIAN:
      case FARM:
      case DISTRIBUTOR:
      case DATE:
        this.resetAllAutoComplete([ this.autocompleteSample ]);
        break;
      case SAMPLE:
        break;
      default:
        console.error('Identifier (', updatedIdentifier, ') not handled in resetAutoCompleteAccordingIdentifier');
        break;
    }
  }

  /**
   * Will reset the auto complete component present in the list
   * @param autoCompleteToReset the list of auto complete to reset
   */
  resetAllAutoComplete(autoCompleteToReset: (AdvancedSampleMultipleAutocompleteComponent<any>|NeqSelectAutocompleteComponent)[]): void {
    autoCompleteToReset.forEach(autoComplete => autoComplete.reset(true, false));
  }

  fetchPredMod = (searchTerm: string, page: number): Promise<SelectionItem<PredictionModelDto>[]> => {
    const filter = new PredictionModelFilterDto();
    if (searchTerm) {
      filter.code = searchTerm;
      filter.label = searchTerm;
    }
    filter.inLevelIds = this.allFilteredSelectedLevels.map(filtersLevel => filtersLevel.id);
    filter.matchAny = true;
    filter.page = page;
    filter.count = NB_ELEMENT_PER_PAGE;
    filter.sort = [{field: 'code', asc: true}];

    return this.convertToPromise(
      this.entityService.predictionModelsFetch(this.userService.getEntity(), filter).pipe(
        tap(result => this.predModSelection.nbTotal = result.total),
        map(result => result.data.map(predictionModel => new SelectionItem<PredictionModelDto>(predictionModel, predictionModel.code, predictionModel.label)))
      )
    );
  };


  fetchReference = (searchTerm: string, page: number): Promise<SelectionItem<ReferenceDto>[]> => {
    const filter = new ReferenceFilterDto();
    if (searchTerm) {
      filter.code = searchTerm;
      filter.label = searchTerm;
    }
    filter.inPredictionModelIds = this.predModSelection.allSelectedValues.map(selected => selected.data.id);
    if (filter.inPredictionModelIds.length === 0) {
      filter.inLevelIds = this.allFilteredSelectedLevels.map(filtersLevel => filtersLevel.id);
    }
    filter.matchAny = true;
    filter.page = page;
    filter.count = NB_ELEMENT_PER_PAGE;
    filter.sort = [{field: 'code', asc: true}];

    return this.convertToPromise(
        this.entityService.referencesFetch(this.entityCode, filter)
        .pipe(
          tap(result => this.refSelection.nbTotal = result.total),
          map(result => result.data.map(reference => new SelectionItem<ReferenceDto>(reference, reference.code, reference.label)))
        )
    );
  };


  fetchBusinessUnit = (searchTerm:string, page: number): Promise<SelectionItem<BusinessUnitDto>[]> =>{
    const filter = new BusinessUnitFilterDto();
    filter.page = page;
    if (searchTerm) {
      filter.codes = [searchTerm];
      filter.label = searchTerm;
    }
    filter.matchAny = true;
    filter.count = NB_ELEMENT_PER_PAGE;
    filter.sort = [{field: 'code', asc: true}];

    return this.convertToPromise(
      this.businessUnitService.searchByEntityCode(this.entityCode, filter)
        .pipe(
          tap(result => this.businessUnitSelection.nbTotal = result.total),
          map(result => result.data.map(item => new SelectionItem<BusinessUnitDto>(item, item.code, item.label)))
        )
    );
  }

  fetchSupplier = (searchTerm:string, page: number): Promise<SelectionItem<SupplierDto>[]> => {
    const allowedInCustomerCodes: string[] = this.entitySelection.allSelectedValues
                                          .filter(item => item.data?.customer?.identifier)
                                          .map(selection => selection.data.customer.identifier);
    if (this.entitySelection.allSelectedValues.length > 0 && allowedInCustomerCodes.length === 0) {
      /* If there is selected entities AND there is not any linked customers to those entities that means that there cannot be
       * any suppliers linked to this entities then no need to send request to back (which will not handle this case by the way) */
      this.supplierSelection.nbTotal = 0;
      return this.convertToPromise(of([]));
    } else {
      const filter = new SupplierFilterDto();
      filter.allowedInCustomerCodes = allowedInCustomerCodes;
      if (searchTerm) {
        filter.label = searchTerm;
        filter.codes = [ searchTerm ];
      }
      /* Compared to business unit the customer is the closest entity to supplier
       * then the customer has the priority on business unit */
      if (allowedInCustomerCodes.length === 0) {
        filter.inBusinessUnitIds = this.businessUnitSelection.allSelectedValues.map(selection => selection.data.id);
      }
      filter.matchAny = true;
      filter.page = page;
      filter.count = NB_ELEMENT_PER_PAGE;
      filter.sort = [{field: 'code', asc: true}];

      return this.convertToPromise(
        this.supplierService.searchByEntityCode(this.entityCode, filter)
          .pipe(
            tap(result => this.supplierSelection.nbTotal = result.total),
            map(result => result.data.map(item => new SelectionItem<SupplierDto>(item, item.code, item.label)))
          )
      );
    }
  }

  fetchAssociatedEntities = (_searchTerm:string, _page: number): Promise<SelectionItem<EntityDto>[]> => {
    const inBusinessUnitCodes: string[] = this.businessUnitSelection.allSelectedValues.map(selection => selection.data.code);

    return this.convertToPromise(
      this.subEntityService.associationsFetch(this.entityCode, true, true, inBusinessUnitCodes)
        .pipe(
          tap(result => this.entitySelection.nbTotal = result.length),
          map(result => result
            .map(item => new SelectionItem<EntityDto>(item, item.code, item.name))
            .sort((item1, item2) => item1.label.localeCompare(item2.label))
          )
        )
    );
  }

  filterEntities = (searchTerm: string, data: EntityDto[]): EntityDto[] => {
    if (searchTerm) {
      const lowerCasedSearchTerm = searchTerm.toLowerCase();
      return data.filter(item => item.name.toLowerCase().includes(lowerCasedSearchTerm) || item.code.toLowerCase().includes(lowerCasedSearchTerm))
    } else {
      return data;
    }
  }

  fecthSample = (searchTerm:string, page: number): Promise<SelectionItem<SampleDashboardDto>[]> => {
    const filter = new SampleFilterDto();
    if (searchTerm) {
      filter.label = searchTerm;
      filter.code = searchTerm;
    }
    SampleAdvancedSearchService.fillSampleFilterAccordingCriteriasForSample(filter, this.returnAdvancedSearchCriteria(), false);
    filter.matchAny = true;
    filter.page = page;
    filter.count = NB_ELEMENT_PER_PAGE;
    filter.sort = [{field: 'code', asc: true}];
    return this.convertToPromise(
      this.sampleService.searchSampleDashboardByEntityCode(this.entityCode, filter, false)
        .pipe(
          tap(result => this.sampleSelection.nbTotal = result.total),
          map(result => result.data.map(item => new SelectionItem<SampleDashboardDto>(item, item.code, item.label)))
        )
      );
  }

  fecthFarmName = (searchTerm:string, page: number): Promise<SelectionItem<string>[]> => {
    const filter = new SampleFilterDto();
    if (searchTerm) {
      filter.farmLabel = searchTerm;
    }
    SampleAdvancedSearchService.fillSampleFilterAccordingCriteriasForSampleMetaData(filter, this.returnAdvancedSearchCriteria());
    filter.matchAny = true;
    filter.page = page;
    filter.count = NB_ELEMENT_PER_PAGE;
    return this.convertToPromise(
      this.sampleService.searchFarmNameByEntityCode(this.entityCode, filter)
        .pipe(
          tap(result => this.farmSelection.nbTotal = result.total),
          map(result => result.data.map(item => new SelectionItem<string>(item, item)))
        )
      );
  }

  fecthDistributors = (searchTerm:string, page: number): Promise<SelectionItem<string>[]> => {
    const filter = new SampleFilterDto();
    if (searchTerm) {
      filter.distributor = searchTerm;
    }
    SampleAdvancedSearchService.fillSampleFilterAccordingCriteriasForSampleMetaData(filter, this.returnAdvancedSearchCriteria());
    filter.matchAny = true;
    filter.page = page;
    filter.count = NB_ELEMENT_PER_PAGE;
    return this.convertToPromise(
      this.sampleService.searchDistributorByEntityCode(this.entityCode, filter)
        .pipe(
          tap(result => this.distributorSelection.nbTotal = result.total),
          map(result => result.data.map(item => new SelectionItem<string>(item, item)))
        )
      );
  }


  fecthTechnician = (searchTerm:string, page: number): Promise<SelectionItem<string>[]> => {
    const filter = new SampleFilterDto();
    if (searchTerm) {
      filter.technicianName = searchTerm;
    }
    SampleAdvancedSearchService.fillSampleFilterAccordingCriteriasForSampleMetaData(filter, this.returnAdvancedSearchCriteria());
    filter.matchAny = true;
    filter.page = page;
    filter.count = NB_ELEMENT_PER_PAGE;
    return this.convertToPromise(
      this.sampleService.searchTechnicianByEntityCode(this.entityCode, filter)
        .pipe(
          tap(result => this.technicianSelection.nbTotal = result.total),
          map(result => result.data.map(item => new SelectionItem<string>(item, item)))
        )
      );
  }

  fecthSpecies = (searchTerm:string, page: number): Promise<SelectionItem<SpeciesDto>[]> => {
    const filter = new SpeciesFilterDto();
    if (searchTerm) {
      filter.code = searchTerm;
      filter.label = searchTerm;
    }
    filter.matchAny = true;
    filter.page = page;
    filter.count = NB_ELEMENT_PER_PAGE;
    return this.convertToPromise(
      this.speciesService.search(filter)
        .pipe(
          tap(result => this.speciesSelection.nbTotal = result.total),
          map(result => result.data.map(item => new SelectionItem<SpeciesDto>(item, item.code, item.label)))
        )
      );
  }

  private convertToPromise<T>(searchObs: Observable<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      searchObs
      .subscribe(
        {
          next:result => resolve(result),
          error : (error) => {
            reject(error);
          }
        }
      );
    })
  }

  /**
   * Return the advanced search criteria which corresponds to the different selection
   * @returns the advanced search
   */
  private returnAdvancedSearchCriteria(): SampleAdvancedSearchCriterias {
    const advancedSearch = new SampleAdvancedSearchCriterias();
    advancedSearch.samples = this.sampleSelection.getSelectedDatas();
    advancedSearch.entities = this.entitySelection.getSelectedDatas();
    advancedSearch.suppliers = this.supplierSelection.getSelectedDatas();
    advancedSearch.businessUnits = this.businessUnitSelection.getSelectedDatas();
    advancedSearch.predictionModels = this.predModSelection.getSelectedDatas();
    advancedSearch.references = this.refSelection.getSelectedDatas();
    advancedSearch.farmNames = this.farmSelection.getSelectedDatas();
    advancedSearch.distributorNames = this.distributorSelection.getSelectedDatas();
    advancedSearch.technicianNames = this.technicianSelection.getSelectedDatas();
    advancedSearch.allSpecies = this.speciesSelection.getSelectedDatas();
    advancedSearch.levels = this.allSelectedLevels;
    advancedSearch.filteredLevels = this.allFilteredSelectedLevels;
    advancedSearch.startValidationDate = this.startValidation ?? null ;
    advancedSearch.endValidationDate = this.endValidation ?? null ;
    advancedSearch.startReceptionDate = this.startReception ?? null ;
    advancedSearch.endReceptionDate = this.endReception ?? null ;
    return advancedSearch;
  }

  returnDateFormat(): string {
    return this.userService.returnUserDateFormat();
  }

  onSelectionSubmit() {
    /* Send the new search criterias to the sample dashboard */
    this.sampleAdvancedSearch.setCurrentAdvancedSearch(this.returnAdvancedSearchCriteria());
    this.dialogRef.close();
  }

  onSingleSampleSelectionChange(event: SelectionItem<SampleDto>): void {
    this.dialogRef.close(event.data);
  }

  onCancel() {
    this.dialogRef.close();
  }

}
